import { CloseRounded, HeadphonesRounded } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { fancyTimeFormat } from './timeHelper';

const DISMISS_AFTER_MS = 10000;

export default function AudioVideoContinuation({ seconds, paused, onContinue, onDismiss }) {
    const [remaining, setRemaining] = useState(DISMISS_AFTER_MS);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [hidden, setHidden] = useState(document.hidden);
    const [windowActive, setWindowActive] = useState(true);
    const dismissRef = useRef(onDismiss);
    dismissRef.current = onDismiss;
    const counting = !paused && !hovered && !focused && !hidden && windowActive;

    useEffect(() => {
        let focusCheck;
        const visibility = () => setHidden(document.hidden);
        const focus = () => setWindowActive(true);
        const blur = () => {
            // A focused YouTube iframe can blur the window while the document
            // remains active. Only pause for an actual switch to another window.
            window.clearTimeout(focusCheck);
            focusCheck = window.setTimeout(() => setWindowActive(document.hasFocus()), 0);
        };
        document.addEventListener('visibilitychange', visibility);
        window.addEventListener('focus', focus);
        window.addEventListener('blur', blur);
        return () => {
            window.clearTimeout(focusCheck);
            document.removeEventListener('visibilitychange', visibility);
            window.removeEventListener('focus', focus);
            window.removeEventListener('blur', blur);
        };
    }, []);

    useEffect(() => {
        if (!counting) return undefined;
        let previous = Date.now();
        const timer = window.setInterval(() => {
            const now = Date.now();
            const elapsed = now - previous;
            previous = now;
            setRemaining(value => Math.max(0, value - elapsed));
        }, 100);
        return () => window.clearInterval(timer);
    }, [counting]);

    useEffect(() => {
        if (remaining === 0) dismissRef.current();
    }, [remaining]);

    return <div className='audioVideoContinuation'
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
        <button type='button' className='audioVideoContinueButton' onClick={onContinue}
            aria-label={`Continue video from audio, about ${fancyTimeFormat(seconds)}`}
            title='Uses your audio progress. Ads or edits may shift timing.'>
            <HeadphonesRounded aria-hidden='true' />
            <span><strong>Continue from audio</strong><span>About {fancyTimeFormat(seconds)}</span></span>
        </button>
        <button type='button' className='audioVideoDismissButton' onClick={onDismiss}
            aria-label='Dismiss audio continuation' title='Keeps your current video position'>
            <svg className='audioVideoCountdown' viewBox='0 0 32 32' aria-hidden='true'>
                <circle cx='16' cy='16' r='14' className='audioVideoCountdownTrack' />
                <circle cx='16' cy='16' r='14' pathLength='1' strokeDasharray='1'
                    strokeDashoffset={1 - remaining / DISMISS_AFTER_MS} />
            </svg>
            <CloseRounded aria-hidden='true' />
        </button>
    </div>;
}
